import { useState } from "react";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@groot/ui/card";
import { Label } from "@groot/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Textarea } from "@groot/ui/textarea";
import { EmptyState } from "@groot/ui/empty-state";
import {
  useAcceptTransferReviewEntry,
  useDismissTransferReviewEntry,
  useTransferReviewQueue,
} from "@/hooks/api";
import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { formatDate } from "@/lib/utils";
import { TransactionType } from "@/types";
import type { TransferReviewQueueItem, TransferReviewStatus } from "@/types";
import { ArrowLeftRight, Check, Loader2, X } from "lucide-react";

/** Tier badges for the queue surface (one per DETECTION_TIERS value on the server). */
const TIER_BADGES: Record<number, string> = {
  1: "Tier 1 · Account identity",
  2: "Tier 2 · Explicit marker",
  3: "Tier 3 · Card payment",
  4: "Tier 4 · Amount + date",
  5: "Tier 5 · Ambiguous",
  6: "Tier 6 · Same-account pass-through",
  7: "Loan / other counter",
};

function tierBadge(tier: number): string {
  return TIER_BADGES[tier] ?? `Tier ${tier}`;
}

function ReviewRow({ item }: { item: TransferReviewQueueItem }) {
  const [reason, setReason] = useState("");
  const accept = useAcceptTransferReviewEntry();
  const dismiss = useDismissTransferReviewEntry();

  const counterpart = item.counterpartTransaction;

  return (
    <TableRow>
      <TableCell className="align-top">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {item.transaction.description ?? item.transaction.payee ?? "Transaction"}
          </p>
          <p className="text-xs text-muted-foreground">
            {item.transaction.accountName} · {formatDate(item.transaction.date)} ·{" "}
            <AmountDisplay amount={Number(item.transaction.amount)} type={TransactionType.DEBIT} />{" "}
            · {item.transaction.type}
          </p>
        </div>
      </TableCell>
      <TableCell className="align-top">
        {counterpart ? (
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {counterpart.description ?? counterpart.payee ?? "Transaction"}
            </p>
            <p className="text-xs text-muted-foreground">
              {counterpart.accountName} · {formatDate(counterpart.date)} ·{" "}
              <AmountDisplay amount={Number(counterpart.amount)} type={TransactionType.CREDIT} /> ·{" "}
              {counterpart.type}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">— ambiguous (multiple candidates)</p>
        )}
      </TableCell>
      <TableCell className="align-top">
        <div className="space-y-1">
          <Badge variant="secondary">{tierBadge(item.tier)}</Badge>
          <p className="max-w-64 text-xs text-muted-foreground">
            {item.evidence?.signal ?? "Detected by the transfer sweep"}
          </p>
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            onClick={() => accept.mutate(item.id)}
            disabled={accept.isPending || !counterpart}
            title={counterpart ? "Link both legs as a transfer" : "No single counterpart to link"}
          >
            {accept.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={dismiss.isPending || reason.trim().length < 3}
            title="Keep the rows unlinked and record why"
            onClick={() => dismiss.mutate({ id: item.id, reason: reason.trim() })}
          >
            {dismiss.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <X className="size-4" />
            )}
            Dismiss
          </Button>
          <Textarea
            aria-label="Dismissal reason"
            placeholder="Why is this not a transfer? (recorded with the entry)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="min-h-16 text-xs"
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

export function TransferReviewQueue() {
  const [status, setStatus] = useState<TransferReviewStatus>("pending");
  const { data: items = [], isLoading } = useTransferReviewQueue(status);

  const pending = items.filter((i) => i.status === "pending");

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowLeftRight className="size-4" /> Transfer review queue
          </CardTitle>
          <CardDescription>
            The transfer sweep auto-applies only what it is certain of (own-account identity,
            explicit markers, card payments). Everything else lands here — accept to link both legs
            as a transfer, or dismiss with a reason.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="w-56">
              <Label htmlFor="queue-status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as TransferReviewStatus)}>
                <SelectTrigger id="queue-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="dismissed">Dismissed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="pb-2 text-xs text-muted-foreground">
              {pending.length} pending candidate{pending.length === 1 ? "" : "s"} · entries expire
              after 90 days
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={ArrowLeftRight}
              title="Nothing to review"
              description={
                status === "pending"
                  ? "No transfer candidates waiting. Run the detection sweep from Settings → Jobs."
                  : `No ${status} entries.`
              }
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction</TableHead>
                    <TableHead>Possible counterpart</TableHead>
                    <TableHead>Signal</TableHead>
                    <TableHead className="w-44">Decision</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <ReviewRow key={item.id} item={item} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
